// Общее состояние редактора формул: активное поле (куда вставляют палитры ленты), запросы «открыть формулу
// для правки» после вставки, словарь символов экспликаций документа.
import type { MathfieldElement } from 'mathlive';
import type { Editor } from '@tiptap/core';
import { NodeSelection, TextSelection } from '@tiptap/pm/state';
import { symKey } from './latex';

type Listener = () => void;

export interface ActiveField {
  mf: MathfieldElement;
  /** 'block' - выключная в документе, 'inline' - строчная, 'dialog' - окно редактора */
  kind: 'block' | 'inline' | 'dialog';
  /** закончить правку (для кнопки «Готово» ленты) */
  commit?: () => void;
}

class MathUI {
  active: ActiveField | null = null;
  /** позиция узла, который надо открыть для правки сразу после вставки */
  pendingPos: number | null = null;
  private ls = new Set<Listener>();
  on(fn: Listener) { this.ls.add(fn); return () => { this.ls.delete(fn); }; }
  emit() { this.ls.forEach((f) => { try { f(); } catch (e) { console.error(e); } }); }
  setActive(a: ActiveField | null) { this.active = a; this.emit(); }
  clearActive(mf: MathfieldElement) { if (this.active?.mf === mf) { this.active = null; this.emit(); } }
  requestEdit(pos: number) { this.pendingPos = pos; this.emit(); }
  /** Узел в позиции pos должен открыться для правки (однократно). */
  takePending(pos: number | undefined): boolean {
    if (pos === undefined || this.pendingPos === null || this.pendingPos !== pos) return false;
    this.pendingPos = null;
    return true;
  }
}
export const mathUI = new MathUI();

/** Вставить шаблон в активное поле формулы. false - активного поля нет. */
export function insertIntoActive(tpl: string): boolean {
  const a = mathUI.active;
  if (!a || !a.mf.isConnected) return false;
  a.mf.executeCommand(['insert', tpl, { selectionMode: 'placeholder', format: 'latex', focus: true } as any]);
  a.mf.focus();
  return true;
}

// ------------------------------------------------------------------------------------------------ курсор
/** Курсор сразу после узла в pos (при необходимости - новый пустой абзац после выключной формулы). */
export function caretAfter(editor: Editor, pos: number, block: boolean) {
  const { state } = editor;
  const node = state.doc.nodeAt(pos);
  if (!node) return;
  const after = pos + node.nodeSize;
  let tr = state.tr;
  if (block) {
    const $a = state.doc.resolve(after);
    const next = $a.nodeAfter;
    // только обычный абзац: заголовок следующего раздела, подпись и т. п. - не место для текста после формулы
    if (!next || next.type.name !== 'paragraph') {
      tr = tr.insert(after, state.schema.nodes.paragraph.create());
    }
    tr = tr.setSelection(TextSelection.create(tr.doc, after + 1));
  } else {
    tr = tr.setSelection(TextSelection.create(tr.doc, after));
  }
  editor.view.dispatch(tr.scrollIntoView());
  editor.view.focus();
}

export function caretBefore(editor: Editor, pos: number, block: boolean) {
  const { state } = editor;
  let tr = state.tr;
  if (block) {
    const $p = state.doc.resolve(pos);
    const prev = $p.nodeBefore;
    if (!prev || prev.type.name !== 'paragraph') {
      tr = tr.insert(pos, state.schema.nodes.paragraph.create());
      tr = tr.setSelection(TextSelection.create(tr.doc, pos + 1));
    } else tr = tr.setSelection(TextSelection.create(tr.doc, pos - 1));
  } else tr = tr.setSelection(TextSelection.create(tr.doc, pos));
  editor.view.dispatch(tr.scrollIntoView());
  editor.view.focus();
}

export function selectNodeAt(editor: Editor, pos: number) {
  try {
    editor.view.dispatch(editor.state.tr.setSelection(NodeSelection.create(editor.state.doc, pos)));
    editor.view.focus();
  } catch { /* */ }
}

// ------------------------------------------------------------------------------------------------ экспликации документа
export interface WhereKnown { sym: string; desc: string; formula: string }

/** Все пояснённые символы документа (символ -> первая расшифровка), до позиции before (если задана). */
export function knownSymbols(editor: Editor | null, before?: number): Map<string, WhereKnown> {
  const out = new Map<string, WhereKnown>();
  if (!editor) return out;
  editor.state.doc.descendants((n, pos) => {
    if (before !== undefined && pos >= before) return false;
    if (n.type.name === 'formula') {
      for (const w of (n.attrs.where || []) as { sym: string; desc: string }[]) {
        const k = symKey(w.sym);
        if (k && w.desc && !out.has(k)) out.set(k, { sym: w.sym, desc: w.desc, formula: n.attrs.id });
      }
    }
    return true;
  });
  return out;
}
